import React from 'react';
import { connect } from 'react-redux';
import { Router } from './App.router';
import { BrowserRouter } from 'react-router-dom';
import { appInit } from 'shared/store/actions/application.actions';
import { MobilePopover } from '../mobilePopover';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { deepLinkTrigger } from 'shared/store/actions/deep-linking.actions';
import analyticsService from '../../helpers/analytics.service';
import remoteConfigService from '../../helpers/remote-config.service';
import { MaintenancePage } from '../../pages/maintenance';
import { getMaintenanceStatus } from 'shared/store/selectors/maintenance.selector';
import { ConfigProvider } from 'antd';
import { colors, antdTheme as theme, colorPalette } from 'shared/constants';
import { AntDesignStylesOverrides } from './AntDesignStylesOverrides';
import { useQuery } from 'src/utils/router.hooks';
import Logger from 'shared/utils/Logger';
import monitorService from 'src/helpers/monitor.service';
import { useOrganizationChangeListener } from 'shared/hooks/organization.hooks';
import { useClearQueryCacheOnOrgChange } from 'shared/hooks/use-clear-query-cache-on-org-change';
import { PageTitleProvider } from './use-page-title';

// Set global
global.platform = 'web';

const App = React.memo(
  ({ appInit = () => {}, isReady = false, deepLinkTrigger = () => {}, inMaintenance = false }) => {
    const query = useQuery();

    React.useEffect(() => {
      const init = async () => {
        if (!isReady) {
          await remoteConfigService.init();

          analyticsService.init();

          Logger.init(monitorService);

          Logger.log('App - Init start');
          appInit();

          // Check if there is a deep link
          if (query.action) {
            deepLinkTrigger(query);
          }
        }
      };

      init();
    }, [isReady, query, appInit, deepLinkTrigger]);

    useOrganizationChangeListener();
    useClearQueryCacheOnOrgChange();

    const renderContent = () => {
      if (!isReady) return null;

      if (inMaintenance) return <MaintenancePage />;

      return <Router />;
    };

    return (
      <BrowserRouter basename={process.env.REACT_APP_ROUTER_BASENAME || ''}>
        <style>
          {`
      :root {
        ${Object.keys(theme)
          .map(key => `--${key}: ${theme[key]};`)
          .join('\n')}

        ${Object.keys(colorPalette)
          .map(key => `--${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${colorPalette[key]};`)
          .join('\n')}
      }
      `}
        </style>
        <MobilePopover>
          <ConfigProvider
            theme={{
              token: {
                colorPrimary: theme.colorPrimary,
                colorError: theme.colorError,
                fontFamily: theme.fontFamily,
                fontSizeHeading3: 22,
                colorTextHeading: colors.darkSlateBlue,
                fontWeightStrong: 700,
              },
            }}>
            <PageTitleProvider>
              <AntDesignStylesOverrides>
                <DndProvider backend={HTML5Backend}>{renderContent()}</DndProvider>
              </AntDesignStylesOverrides>
            </PageTitleProvider>
          </ConfigProvider>
        </MobilePopover>
      </BrowserRouter>
    );
  },
);

const mapStateToProps = ({ application, maintenance }) => ({
  isReady: application.isReady,
  inMaintenance: getMaintenanceStatus({ maintenance }),
});

export default connect(mapStateToProps, { appInit, deepLinkTrigger })(App);
